import type BaseEnumType from "@/app/services/baseEnumType";
import { EnumEprMaterialType } from "@/app/services/generated/enums/enumEprMaterialType.generated";
import { EnumEprPackagingClass } from "@/app/services/generated/enums/enumEprPackagingClass.generated";
import { EnumEprPackagingType } from "@/app/services/generated/enums/enumEprPackagingType.generated";
import { EnumEprRecyclabilityAssessmentRating } from "@/app/services/generated/enums/enumEprRecyclabilityAssessmentRating.generated";
import type { BomLine } from "@/app/services/generated/eprbomService";
import type { Maybe } from "@/components/utils/typeHelpers";
import type { ControlIn } from "@/controls/control";
import { Control, extendIn } from "@/controls/control";
import { ddls } from "@/controls/ddls";
import { inputs } from "@/controls/inputs";
import { layouts } from "@/controls/layouts";
import { keys } from "@/plugins/i18n";
import type { ShallowReactive } from "vue";
import { watchEffect } from "vue";

export class EnumPlasticMaterialSubtype implements BaseEnumType {
  private static allValues: EnumPlasticMaterialSubtype[] = []; // make sure this is top-most

  public static rigid = new EnumPlasticMaterialSubtype(
    0,
    "Rigid",
    "material_subtype_rigid"
  );
  public static flexible = new EnumPlasticMaterialSubtype(
    1,
    "Flexible",
    "material_subtype_flexible"
  );

  public key: number;
  public value: string;
  public translationKey?: string;

  private constructor(key: number, value: string, translationKey?: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumPlasticMaterialSubtype.allValues.push(this);
  }

  public equals(other: Maybe<EnumPlasticMaterialSubtype>): boolean {
    return this.key === other?.key;
  }

  public isRigid() {
    return this.equals(EnumPlasticMaterialSubtype.rigid);
  }
  public isFlexible() {
    return this.equals(EnumPlasticMaterialSubtype.flexible);
  }

  public static getValues(): EnumPlasticMaterialSubtype[] {
    return EnumPlasticMaterialSubtype.allValues;
  }

  public static getByKey(key: number): EnumPlasticMaterialSubtype {
    const found = EnumPlasticMaterialSubtype.allValues.find(
      (e) => e.key === key
    );
    if (found === undefined) {
      throw new Error(`EnumStockType - Key not found: ${key}`);
    }

    return found;
  }
}

export class EnumOtherMaterialSubtype implements BaseEnumType {
  private static allValues: EnumOtherMaterialSubtype[] = []; // make sure this is top-most

  public static bamboo = new EnumOtherMaterialSubtype(
    0,
    "Bamboo",
    "material_subtype_bamboo"
  );
  public static ceramic = new EnumOtherMaterialSubtype(
    1,
    "Ceramic",
    "material_subtype_ceramic"
  );
  public static copper = new EnumOtherMaterialSubtype(
    2,
    "Copper",
    "material_subtype_copper"
  );
  public static cork = new EnumOtherMaterialSubtype(
    3,
    "Cork",
    "material_subtype_cork"
  );
  public static hemp = new EnumOtherMaterialSubtype(
    4,
    "Hemp",
    "material_subtype_hemp"
  );
  public static rubber = new EnumOtherMaterialSubtype(
    5,
    "Rubber",
    "material_subtype_rubber"
  );
  public static silicone = new EnumOtherMaterialSubtype(
    6,
    "Silicone",
    "material_subtype_silicone"
  );

  public key: number;
  public value: string;
  public translationKey?: string;

  private constructor(key: number, value: string, translationKey?: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumOtherMaterialSubtype.allValues.push(this);
  }

  public equals(other: Maybe<EnumOtherMaterialSubtype>): boolean {
    return this.key === other?.key;
  }

  public isBamboo() {
    return this.equals(EnumOtherMaterialSubtype.bamboo);
  }
  public isCeramic() {
    return this.equals(EnumOtherMaterialSubtype.ceramic);
  }
  public isCopper() {
    return this.equals(EnumOtherMaterialSubtype.copper);
  }
  public isCork() {
    return this.equals(EnumOtherMaterialSubtype.cork);
  }
  public isHemp() {
    return this.equals(EnumOtherMaterialSubtype.hemp);
  }
  public isRubber() {
    return this.equals(EnumOtherMaterialSubtype.rubber);
  }
  public isSilicone() {
    return this.equals(EnumOtherMaterialSubtype.silicone);
  }

  public static getValues(): EnumOtherMaterialSubtype[] {
    return EnumOtherMaterialSubtype.allValues;
  }

  public static getByKey(key: number): EnumOtherMaterialSubtype {
    const found = EnumOtherMaterialSubtype.allValues.find((e) => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumStockType - Key not found: ${key}`);
    }

    return found;
  }
}

type BomLineSectionIn = ControlIn & {};

export class BomLineSection extends Control {
  #in: ShallowReactive<BomLineSectionIn>;
  get in() {
    return this.#in;
  }

  private _mainLayoutArea = layouts.vStackSpaced();

  private _nameTb = inputs.text({
    translateKey: keys.name,
  });
  private _packagingTypeDdl = ddls.enum(EnumEprPackagingType, {
    translateKey: keys.epr_packaging_type,
  });
  private _packagingClassDdl = ddls.enum(EnumEprPackagingClass, {
    translateKey: keys.epr_packaging_class,
  });
  private _materialTypeDdl = ddls.enum(EnumEprMaterialType, {
    translateKey: keys.epr_material_type,
  });
  private _plasticMaterialSubtypeDdl = ddls.enum(EnumPlasticMaterialSubtype, {
    translateKey: keys.epr_material_subtype,
  });
  private _otherMaterialSubtypeDdl = ddls.enum(EnumOtherMaterialSubtype, {
    translateKey: keys.epr_material_subtype,
  });
  private _weightKg = inputs.number({ translateKey: keys.weight_kg, min: 0 });
  private _quantity = inputs.numberQ({
    translateKey: keys.qty_if_drinks_container,
    min: 0,
  });
  private _recyclabilityRatingDdl = ddls.enum(
    EnumEprRecyclabilityAssessmentRating,
    {
      translateKey: keys.recyclability_assessment_rating,
    }
  );

  render(h: any) {
    return this._mainLayoutArea.render(h);
  }

  constructor() {
    super();

    this.#in = extendIn(super.in, {
      readOnly: false,
    });

    this._mainLayoutArea.addChildren([
      this._nameTb,
      this._packagingTypeDdl,
      this._packagingClassDdl,
      this._materialTypeDdl,
      this._plasticMaterialSubtypeDdl,
      this._otherMaterialSubtypeDdl,
      this._weightKg,
      this._quantity,
      this._recyclabilityRatingDdl,
    ]);

    watchEffect(() => {
      this._quantity.in.visible =
        this._packagingTypeDdl.val.isDrinksContainer();
      this._plasticMaterialSubtypeDdl.in.visible =
        this._materialTypeDdl.val.isPlastic();
      this._otherMaterialSubtypeDdl.in.visible =
        this._materialTypeDdl.val.isOther();
    });
  }

  loadFromBomLine(line: BomLine) {
    this._nameTb.setValue(line.name);
    this._packagingTypeDdl.setValue(line.packagingType);
    this._packagingClassDdl.setValue(line.packagingClass);
    this._materialTypeDdl.setValue(line.materialType);

    const plasticSubtype = EnumPlasticMaterialSubtype.getValues().find(
      (x) => x.value === line.materialSubtype
    );
    const otherSubtype = EnumOtherMaterialSubtype.getValues().find(
      (x) => x.value === line.materialSubtype
    );
    if (this._materialTypeDdl.val.isPlastic() && plasticSubtype) {
      this._plasticMaterialSubtypeDdl.setValue(plasticSubtype);
    } else if (this._materialTypeDdl.val.isOther() && otherSubtype) {
      this._otherMaterialSubtypeDdl.setValue(otherSubtype);
    }

    this._weightKg.setValue(line.weightKg);
    this._quantity.setValue(line.quantityOfDrinksContainers);
    this._recyclabilityRatingDdl.setValue(line.recyclabilityAssessmentRating);
  }

  writeToBomLine(line: BomLine) {
    line.name = this._nameTb.val;
    line.packagingType = this._packagingTypeDdl.val;
    line.packagingClass = this._packagingClassDdl.val;
    line.materialType = this._materialTypeDdl.val;

    if (this._materialTypeDdl.val.isPlastic()) {
      line.materialSubtype = this._plasticMaterialSubtypeDdl.val.value;
    } else if (this._materialTypeDdl.val.isOther()) {
      line.materialSubtype = this._otherMaterialSubtypeDdl.val.value;
    } else {
      line.materialSubtype = "";
    }

    line.weightKg = this._weightKg.val;
    line.quantityOfDrinksContainers = this._quantity.val ?? 0;
    line.recyclabilityAssessmentRating = this._recyclabilityRatingDdl.val;
  }
}
